<template>
  <div class="py-3">
    <!-- guideline -->
    <b-row class="my-2">
      <b-col>
        <p>{{ $t('scoringProfilesModelEdit.driversGuideline') }}</p>
      </b-col>
    </b-row>
    <!-- drivers -->
    <template v-if="Object.keys(drivers).length > 0">
      <b-row class="mb-3" v-for="(row, rowIndex) in rows()" :key="rowIndex">
        <b-col class="col-content" cols="6" v-for="(key, colIndex) in row" :key="colIndex">
          <div class="driver-family-wrapper">
            <scoring-profiles-driver-family
              :title="key"
              :list="drivers[key]"
              :disable-selection="getScoringProfileAssociated.length > 0"
              :resetLabel="$t('scoringProfilesModelEdit.resetAll')"
              :input-placeholder="$t('common.weight')"
              :keypress-callback="preventCharsCode"
              :name-formatter-callback="nameFormatter"
              :value-formatter-callback="valueFormatter"
              :default-selected-value="setDefaultSelected(key)"
              @on-reset="onReset(drivers[key])"
              @on-select="onSelect(drivers[key])"
              @on-focus="formatWeight(...arguments, drivers[key])"
              @on-item-select="onItemSelect(...arguments, drivers[key])"
            >
              <template v-slot:tooltip="slotProps" v-if="!isValidMaxTotalWeight(drivers[key])">
                <custom-tooltip
                  :index="slotProps.index"
                  :section="slotProps.family.toLowerCase()"
                  :css-class="['align-middle', 'pointer', 'ml-1']"
                  :tooltipText="$t('scoringProfilesModelEdit.tooltipWeightIndicators')"
                ></custom-tooltip>
              </template>
            </scoring-profiles-driver-family>
          </div>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { preventCharsCode, isNearlyEqual } from '../../utils/formatter'
import { calculateDriversWeightsLists, getMaxTotalDriversWeight } from '../../utils/drivers'
import { SCORING_DEFINITION_TYPES } from '../../settings/scoringProfilesSettings'
const { mapActions, mapGetters } = createNamespacedHelpers('scoringProfilesModelsEditing')
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import ScoringProfilesDriverFamily from '../../components/ScoringProfiles/ScoringProfilesDriverFamily.vue'

export default {
  components: {
    CustomTooltip,
    ScoringProfilesDriverFamily,
  },

  data() {
    return {
      itemsPerRow: 2,
    }
  },

  computed: {
    ...mapGetters(['getScoringProfileModelDrivers', 'getScoringProfileAssociated']),

    drivers() {
      return this.getScoringProfileModelDrivers || {}
    },

    selectedScoringDefinition() {
      return this.$store.getters['scoringProfilesDefinition/getSelectedScoringDefinition']
    },
  },

  methods: {
    preventCharsCode,
    ...mapActions(['setScoringProfileModelFamiliesWeights']),

    rows() {
      const res = []
      const arr = Object.keys(this.drivers)
      for (let i = 0; i < arr.length; i += this.itemsPerRow) {
        const chunk = arr.slice(i, i + this.itemsPerRow)
        res.push(chunk)
      }
      return res
    },

    formatWeight(params, list) {
      const val = !params.item.weight ? null : params.item.weight.toString().replace('%', '')
      list[params.i].weight = val
    },

    nameFormatter(key, value) {
      return this.$t(`drivers.${key}.` + value.toUpperCase())
    },

    valueFormatter(value) {
      if (!value) return null
      const maxLimit = 100
      const float = Number(parseFloat(value.replace('%', '')).toFixed(2))
      value = float > maxLimit ? maxLimit : float
      return value.toString().includes('%') ? value : value + '%'
    },

    isValidMaxTotalWeight(list) {
      const weights = list.map((k) => {
        return {
          ...k,
          weight: !k.weight ? 0 : Number(parseFloat(k.weight).toFixed(2)),
        }
      })
      const sum = getMaxTotalDriversWeight(weights)
      return isNearlyEqual(sum, 100, 0.0001)
    },

    onSelect(list) {
      return list.forEach((el) => {
        el.selected = false
        el.weight = null
      })
    },

    onReset(list) {
      return list.forEach((el) => {
        el.selected = false
        el.weight = null
      })
    },

    onItemSelect(i, list) {
      const val = list[i].weight === '' ? null : list[i].weight
      return (list[i].weight = val)
    },

    setDefaultSelected(key) {
      return this.drivers[key].filter((d) => d.selected).length > 0
    },
  },

  watch: {
    drivers: {
      handler(newVal) {
        let drivers = []
        let selectedFamilies = []

        const keys = Object.keys(newVal)
        if (!Array.isArray(keys) || keys.length === 0) return

        keys.forEach((key) => {
          const familyIndicators = newVal[key].filter((j) => j.selected)
          if (familyIndicators.length > 0) {
            selectedFamilies.push({
              family: key,
              familyIndicators: familyIndicators,
              isValidWeight: this.isValidMaxTotalWeight(familyIndicators),
            })
          }
        })
        this.setScoringProfileModelFamiliesWeights(selectedFamilies)

        selectedFamilies.forEach((j) => {
          const data = {
            family: j.family,
            selected: true,
            weight: null,
          }
          if (this.selectedScoringDefinition.config === SCORING_DEFINITION_TYPES.custom) {
            const i = this.selectedScoringDefinition.list.findIndex((w) => (w.driver || w.family) === j.family)
            if (i !== -1) {
              data.weight = this.selectedScoringDefinition.list[i].weight
            }
          }
          drivers.push(data)
        })

        const list = calculateDriversWeightsLists(
          null,
          drivers,
          this.selectedScoringDefinition.config,
          this.$store.getters['scoringProfilesDefinition/getSequenceWeightsDecay'],
          null
        )
        this.$store.dispatch('scoringProfilesDefinition/setDriversList', list)
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.col-content {
  display: table;
}

.col-content > .driver-family-wrapper {
  display: table-cell;
  width: 33% !important;
  height: auto;
  padding-bottom: 4rem;
}

.driver-family-wrapper {
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid var(--gray8);
}
</style>
